import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, LinearProgress, Stack, Typography } from '@mui/material';
import { getPublicLaunchStatus, retryPublicLaunch } from '../api/publicRegistrationApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { PublicOnboardingShell } from '../components/PublicOnboardingShell';
import { RegisterWizardHeader } from '../components/RegisterWizardHeader';
import { LaunchModulesCard } from '../components/register/LaunchModulesCard';
import { LaunchNextStepsCard } from '../components/register/LaunchNextStepsCard';
import { ProvisioningRunCard } from '../components/register/ProvisioningRunCard';
import { ProvisioningStepList } from '../components/register/ProvisioningStepList';
import { RegisterSectionCard } from '../components/register/RegisterSectionCard';
import { SafeProvisioningLog } from '../components/register/SafeProvisioningLog';
import { TenantAccessCard } from '../components/register/TenantAccessCard';
import { REGISTER_WIZARD_MAX_WIDTH } from '../components/register/registerSurfaces';
import { Button } from '../components/ui/Button';
import {
  buildLaunchStageStates,
  isCanonicalDoneLaunchStatus,
  isCanonicalPublicReadyStatus,
  isLocalSimulatedLaunchStatus,
  isTerminalLaunchStatus,
  resolveLaunchProgressPercent,
} from '../utils/publicLaunchStages';
import { recordLaunchPollTrace } from '../utils/launchPollTrace';
import {
  STEP_META,
  buildProvisioningLogLines,
  loadLaunchContext,
} from '../utils/registerWizardModel';

const { semantic } = colorTokens;

const POLL_MS = 2000;
const ERROR_POLL_MS = 4000;

/**
 * Wizard steps 5 (provisioning) and 6 (done) on /register/status/:publicId.
 * Read-only: this route polls GET launch status and never posts a registration.
 */
export function PublicRegisterLaunchStatusPage() {
  const { publicId } = useParams();
  const { t } = useTranslation(['onboarding', 'common', 'auth']);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [copied, setCopied] = useState(false);
  // Keep latest t without re-subscribing the poll effect (i18n identity churn
  // used to cancel in-flight polls and leave inFlightRef stuck true).
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    let cancelled = false;
    let timer;
    let inFlight = false;

    async function poll() {
      if (!publicId || cancelled || inFlight) {
        return;
      }
      inFlight = true;
      try {
        const data = await getPublicLaunchStatus(publicId);
        if (cancelled) {
          recordLaunchPollTrace({
            status: data?.status,
            public_ready: data?.public_ready,
            progress_percent: data?.progress_percent,
            public_url: data?.public_url,
            state_applied: false,
            cancelled: true,
            terminal: false,
            poll_scheduled: false,
          });
          return;
        }
        setStatus(data);
        setError(null);
        setLoading(false);
        const terminal = isTerminalLaunchStatus(data?.status, data?.public_ready, data?.public_url);
        const schedule = !terminal;
        recordLaunchPollTrace({
          status: data?.status,
          public_ready: data?.public_ready,
          progress_percent: data?.progress_percent,
          public_url: data?.public_url,
          state_applied: true,
          cancelled: false,
          terminal,
          poll_scheduled: schedule,
        });
        if (schedule) {
          timer = setTimeout(poll, POLL_MS);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(tRef.current('onboarding:register.launch_status_error'));
        setLoading(false);
        recordLaunchPollTrace({
          status: 'poll_error',
          public_ready: false,
          progress_percent: null,
          public_url: null,
          state_applied: false,
          cancelled: false,
          terminal: false,
          poll_scheduled: true,
        });
        timer = setTimeout(poll, ERROR_POLL_MS);
      } finally {
        inFlight = false;
      }
    }

    poll();
    return () => {
      cancelled = true;
      inFlight = false;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [publicId]);

  async function onRetry() {
    if (!publicId) {
      return;
    }
    const capability = typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(`h05f_retry_${publicId}`)
      : null;
    if (!capability) {
      setError(t('onboarding:register.launch_retry_unauthorized'));
      return;
    }
    setRetrying(true);
    try {
      const data = await retryPublicLaunch(publicId, capability);
      if (data?.retry_capability && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(`h05f_retry_${publicId}`, data.retry_capability);
      }
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(t('onboarding:register.launch_retry_error'));
    } finally {
      setRetrying(false);
    }
  }

  const progress = resolveLaunchProgressPercent(status);
  const stages = useMemo(
    () => buildLaunchStageStates(status?.status, status?.progress_percent),
    [status?.status, status?.progress_percent],
  );
  const logLines = useMemo(
    () => buildProvisioningLogLines(status, t),
    [status, t],
  );
  const launchContext = useMemo(() => loadLaunchContext(publicId), [publicId]);

  const failed = status?.status === 'failed';
  const localReady = isLocalSimulatedLaunchStatus(status?.status);
  const canonicalReady = isCanonicalPublicReadyStatus(status?.status);
  // Step 6 Done is canonical READY + public_ready only. local_simulated_* stays on Step 5.
  const showDone = isCanonicalDoneLaunchStatus(status?.status, status?.public_ready);
  const stepKey = showDone ? 'done' : 'provisioning';
  const stepMeta = STEP_META[stepKey];

  async function copyPublicUrl() {
    if (!status?.public_url || typeof navigator?.clipboard?.writeText !== 'function') {
      return;
    }
    try {
      await navigator.clipboard.writeText(status.public_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <PublicOnboardingShell
      maxWidth="md"
      maxWidthPx={REGISTER_WIZARD_MAX_WIDTH}
      testId="public-register-launch-shell"
    >
      <RegisterWizardHeader
        stepKey={stepKey}
        title={t(`onboarding:register.wizard.${stepKey}_title`)}
        subtitle={t(`onboarding:register.wizard.${stepKey}_subtitle`)}
        stepLabel={t('onboarding:register.wizard.step_label', {
          current: stepMeta.index,
          total: stepMeta.total,
        })}
        technicalProgressPercent={stepKey === 'provisioning' ? progress : null}
        wizardProgressLabel={t('onboarding:register.wizard.wizard_progress')}
        technicalProgressLabel={t('onboarding:register.wizard.tech_progress')}
      />

      <Stack spacing={2.5}>
        {loading && !status ? (
          <Box role="status" aria-busy="true" aria-label={t('common:loading')}>
            <LinearProgress
              variant="indeterminate"
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: semantic.action.secondary.bg,
                '& .MuiLinearProgress-bar': { bgcolor: semantic.status.brand.border },
              }}
            />
          </Box>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}

        {status ? (
          <>
            {failed ? (
              <Alert severity="error" data-testid="register-launch-failed">
                {t('onboarding:register.launch_failed')}
              </Alert>
            ) : null}
            {/* Canonical production READY only. local_simulated_* terminals stay
                labelled as a local simulation. */}
            {canonicalReady ? (
              <Alert severity="success" data-testid="register-launch-ready">
                {t('onboarding:register.launch_ready')}
              </Alert>
            ) : null}
            {localReady ? (
              <Alert severity="info" data-testid="register-launch-local-ready">
                {t('onboarding:register.launch_local_ready')}
              </Alert>
            ) : null}

            {!showDone ? (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: typographyTokens.fontFamily,
                      fontSize: 13,
                      color: semantic.text.secondary,
                    }}
                  >
                    {t('onboarding:register.launch_overall_progress')}
                  </Typography>
                  <Typography
                    data-testid="register-launch-progress-value"
                    sx={{
                      fontFamily: typographyTokens.fontFamilyDisplay,
                      fontWeight: typographyTokens.fontWeightSemibold,
                      fontSize: 14,
                      color: semantic.text.primary,
                    }}
                  >
                    {progress}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                  aria-label={t('onboarding:register.launch_overall_progress')}
                  data-testid="register-launch-progress"
                  sx={{
                    height: 10,
                    borderRadius: 999,
                    bgcolor: semantic.action.secondary.bg,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: failed ? semantic.error : semantic.status.brand.border,
                    },
                  }}
                />
              </Box>
            ) : null}

            <Typography
              data-testid="register-launch-status"
              data-status={status.status}
              sx={{ fontSize: 13, color: semantic.text.muted }}
            >
              {t('onboarding:register.launch_status_label')}
              {': '}
              {t(`onboarding:register.launch_status.${status.status}`, status.status)}
            </Typography>

            {!showDone ? (
              <Stack spacing={2.5} data-testid="register-step-provisioning">
                <ProvisioningStepList stages={stages} />
                <SafeProvisioningLog lines={logLines} />
                <ProvisioningRunCard status={status} progress={progress} />
                <LaunchModulesCard context={launchContext} />
                <RegisterSectionCard testId="register-launch-next-step-info">
                  <Typography sx={{ fontSize: 14, color: semantic.text.secondary }}>
                    {t('onboarding:register.wizard.next_step_info')}
                  </Typography>
                </RegisterSectionCard>
              </Stack>
            ) : null}

            {failed ? (
              <Button
                variant="Primary"
                onClick={onRetry}
                disabled={retrying || !status.can_retry}
                loading={retrying}
                data-testid="register-launch-retry"
              >
                {t('onboarding:register.launch_retry')}
              </Button>
            ) : null}

            {showDone ? (
              <Stack spacing={2.5} data-testid="register-step-done">
                <Stack spacing={2.5} data-testid="register-launch-done">
                <RegisterSectionCard
                  title={t('onboarding:register.wizard.done_success_title')}
                  testId="register-launch-success"
                >
                  <Typography sx={{ fontSize: 14, color: semantic.text.secondary }}>
                    {t('onboarding:register.wizard.done_success_message')}
                  </Typography>
                </RegisterSectionCard>

                <TenantAccessCard
                  publicUrl={status.public_url}
                  canonicalReady={canonicalReady}
                  copied={copied}
                  onCopy={copyPublicUrl}
                />

                <LaunchModulesCard context={launchContext} activated />
                <ProvisioningRunCard status={status} progress={progress} />
                <SafeProvisioningLog lines={logLines} />

                <RegisterSectionCard
                  title={t('onboarding:register.wizard.final_summary_title')}
                  testId="register-launch-final-summary"
                >
                  <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                    {t('onboarding:register.wizard.final_summary')}
                  </Typography>
                </RegisterSectionCard>

                <LaunchNextStepsCard />
                </Stack>
              </Stack>
            ) : null}
          </>
        ) : null}

        {/* Post-submit: never route back into editable wizard steps. */}
        <Button component={RouterLink} to="/login" variant="Ghost">
          {t('auth:go_to_login')}
        </Button>
      </Stack>
    </PublicOnboardingShell>
  );
}
